import mixpanel, { type Config, type Mixpanel } from "mixpanel-browser";

export type AnalyticsPage = {
  routeId: string;
  projectKey?: string;
  conceptKey?: string;
  creativeKey?: string;
  specificationKey?: string;
  revisionSequenceId?: string;
  generativeEditKey?: string;
};
/**
 * Events are sent for a validated principal, or for an unauthenticated visitor (Auth0 has
 * confirmed there is no session). With neither, the session is unresolved and nothing is sent. User attributes
 * are joined onto the principal key downstream, so none are sent from here.
 */
export type AnalyticsContext = {
  /** Tracking consent; while false nothing initialises, identifies or sends. */
  trackingAllowed: boolean;
  principalKey?: string;
  /** Ignored when a principal is present. */
  unauthenticated?: boolean;
  environment?: string;
  /** Local deduplication only, never sent: a new pathname is a new page view. */
  pathname: string;
  page: AnalyticsPage;
};
type EventProperties = {
  "Page Viewed": AnalyticsPage;
  "Deliverable Download Clicked": { deliverableIntegrationKey: string };
  "Project ZIP Download Clicked": { projectIntegrationKey: string };
};
type EventName = keyof EventProperties;
type AnalyticsConfiguration = { token?: string; region?: string };

const DESTINATIONS: Record<string, string> = {
  US: "https://api-js.mixpanel.com",
  EU: "https://api-eu.mixpanel.com",
  IN: "https://api-in.mixpanel.com",
};
const EVENTS = new Set<EventName>([
  "Page Viewed",
  "Deliverable Download Clicked",
  "Project ZIP Download Clicked",
]);
const PROPERTIES = new Set([
  "token",
  "distinct_id",
  "$user_id",
  "$device_id",
  "$insert_id",
  "time",
  "routeId",
  "projectKey",
  "conceptKey",
  "creativeKey",
  "specificationKey",
  "revisionSequenceId",
  "generativeEditKey",
  "deliverableIntegrationKey",
  "projectIntegrationKey",
  "is_authenticated",
  "environment",
]);

/** SDK-added URLs, referrers, campaigns and arbitrary superproperties never leave this boundary. */
const beforeSend: NonNullable<Config["hooks"]["before_send_events"]> = (event) => {
  if (!EVENTS.has(event.event as EventName)) return null;
  return {
    ...event,
    properties: Object.fromEntries(
      Object.entries(event.properties).filter(
        ([key, value]) => PROPERTIES.has(key) && value != null,
      ),
    ),
  };
};

/** Without consent there is no visitor, so withdrawing consent resets the SDK like a sign-out. */
const visitorOf = (context?: AnalyticsContext) => {
  if (!context?.trackingAllowed) return undefined;
  if (context.principalKey) return `principal:${context.principalKey}`;
  return context.unauthenticated ? "unauthenticated" : undefined;
};

export function createProductAnalytics() {
  let configuration: AnalyticsConfiguration = {};
  let sdk: Mixpanel | undefined;
  let context: AnalyticsContext | undefined;
  /** The visitor the SDK is set up for, and the distinct id its events carry. */
  let active: { visitor: string; distinctId: string } | undefined;
  let failed = false;
  let lastPage: string | undefined;

  const safely = (action: () => void) => {
    try {
      action();
    } catch {
      /* Telemetry must never change product behavior. */
    }
  };

  // An unresolved session keeps the active visitor but sends nothing.
  const sending = () => !failed && active !== undefined && visitorOf(context) !== undefined;

  const send = <E extends EventName>(name: E, properties: EventProperties[E]) => {
    if (!sdk || !sending()) return;
    safely(() =>
      sdk?.track(name, {
        ...properties,
        is_authenticated: Boolean(context?.principalKey),
        ...(context?.environment ? { environment: context.environment } : {}),
      }),
    );
  };

  const reset = () => {
    context = undefined;
    active = undefined;
    try {
      sdk?.reset();
    } catch {
      failed = true;
    }
  };

  const initialize = () => {
    if (sdk) return sdk;
    const token = configuration.token?.trim();
    const destination = configuration.region && DESTINATIONS[configuration.region];
    if (!token || !destination) return undefined;
    const config = {
      api_host: destination,
      autocapture: false,
      track_pageview: false,
      flags: false,
      remote_settings_mode: "disabled",
      record_sessions_percent: 0,
      record_heatmap_data: false,
      record_canvas: false,
      record_console: false,
      record_network: false,
      recording_event_triggers: {},
      track_marketing: false,
      save_referrer: false,
      store_google: false,
      skip_first_touch_marketing: true,
      stop_utm_persistence: true,
      ip: false,
      // No stored device identity, persisted properties/queues, or cross-tab coordination:
      // an unauthenticated visitor's device id lives only as long as the page.
      disable_persistence: true,
      batch_requests: false,
      hooks: { before_send_events: beforeSend },
      // 2.84 implements track_marketing but omits it from its bundled Config type.
    } satisfies Partial<Config> & { track_marketing: boolean };
    sdk = mixpanel.init(token, config, "cloud-product");
    // Unauthenticated history is never merged into a principal. identify still sets distinct_id/$user_id.
    sdk.disable(["$identify", "$create_alias"]);
    return sdk;
  };

  const activate = (principalKey: string | undefined, visitor: string) => {
    if (failed) return;
    try {
      const instance = initialize();
      if (!instance) return;
      if (principalKey) {
        instance.identify(principalKey);
        if (instance.get_distinct_id() !== principalKey) {
          failed = true;
          return;
        }
      }
      active = { visitor, distinctId: instance.get_distinct_id() };
    } catch {
      failed = true;
    }
  };

  return {
    configure(next: AnalyticsConfiguration) {
      reset();
      lastPage = undefined;
      configuration = next;
      sdk = undefined;
      failed = false;
    },
    reset,
    /** The principal key, or the unauthenticated visitor's device id; undefined while nothing would be sent. */
    getDistinctId: () => (sending() ? active?.distinctId : undefined),
    update(next: AnalyticsContext) {
      const visitor = visitorOf(next);
      // Withdrawn consent resets like a sign-out. Otherwise there is no visitor only while the
      // session is unresolved (every token renewal reads Me), and the active one is kept.
      if (!visitor) {
        if (!next.trackingAllowed && active) reset();
        context = next;
        return;
      }
      if (active && active.visitor !== visitor) reset();
      context = next;
      if (!active) activate(next.principalKey, visitor);
      if (!active) return;
      const pageIdentity = JSON.stringify([visitor, next.pathname]);
      if (lastPage !== pageIdentity) {
        lastPage = pageIdentity;
        send("Page Viewed", next.page);
      }
    },
    track<E extends EventName>(name: E, properties: EventProperties[E]) {
      send(name, properties);
    },
  };
}

export const productAnalytics = createProductAnalytics();
